'use client';

/**
 * Static icon tile for the "All Games" grid, linking to `/games/{gameId}/`.
 */

import Image from 'next/image';
import type {WebArcadeGame} from '@/services/web-arcade/types';
import {trackArcadeEvent, ARCADE_EVENT} from './trackArcadeEvent';

interface GameTileProps {
  game: WebArcadeGame;
  rank?: number;
  priority?: boolean;
}

const GameTile = ({game, rank = 0, priority = false}: GameTileProps) => {
  const {gameId, title, subtitle, iconThumbnail} = game;

  return (
    <a
      href={`/games/${gameId}/`}
      aria-label={title}
      onClick={() =>
        trackArcadeEvent(ARCADE_EVENT.TILE_CLICKED, {
          result: JSON.stringify({game_id: gameId, rank, section: 'all games'}),
        })
      }
      className='
        group flex w-full flex-col items-center gap-2
        outline-none focus-visible:ring-2 focus-visible:ring-primary-blue
        focus-visible:ring-offset-2 rounded-xl
      '
    >
      <div
        className='
          relative aspect-square w-full overflow-hidden rounded-xl
          bg-light-gray dark:bg-tile-dark
          shadow-md transition-transform duration-200
          group-hover:scale-[1.02] group-active:scale-[0.98]
        '
      >
        <Image
          src={iconThumbnail}
          alt={title}
          fill
          sizes='(max-width: 600px) 33vw, 200px'
          priority={priority}
          className='object-cover'
        />
      </div>

      <div className='flex w-full flex-col items-center px-1 text-center'>
        <span
          className='
            line-clamp-2 w-full text-[12px] font-bold leading-[16px]
            text-primary-dark dark:text-white
            sm:text-[14px] sm:leading-[18px]
          '
        >
          {title}
        </span>
        {subtitle ? (
          <span
            className='
              line-clamp-1 w-full text-[10px] leading-[14px]
              text-primary-dark/70 dark:text-white/70
              sm:text-[12px] sm:leading-[16px]
            '
          >
            {subtitle}
          </span>
        ) : null}
      </div>
    </a>
  );
};

export default GameTile;
